import { SiteAnalyticsTab } from '../components/SiteAnalytics/siteAnalyticsConstants.ts';

export type DateRangeType = '7D' | '1M' | '3M' | 'YTD' | 'AllTime' | 'Custom';
export type DateRangeShortcutType = Extract<DateRangeType, '7D' | '1M' | '3M' | 'YTD'>;
export type DateRangePayload =
	| { range: DateRangeShortcutType }
	| {
			range: 'Custom';
			fromDate: Date;
			toDate: Date;
	  }
	| {
			range: 'AllTime';
			fromDate: Date;
	  };
export type DateRangeState = {
	dateRange: DateRangeType | null;
	fromDate: Date;
	toDate: Date;
};

export enum ActorType {
	All = 'all',
	Agent = 'agent',
	People = 'people',
}

export type AnalyticsActorType = 'ALL' | 'AGENTS' | 'PEOPLE';
export type PageAnalyticsActorTypeQueryValue = AnalyticsActorType;
export type SiteAnalyticsActivityType = ActorType.Agent | ActorType.People | ActorType.All;
export type SiteAnalyticsActivityTypeQueryValue = 'AGENTS' | 'PEOPLE' | 'AGENTS,PEOPLE';

export const DEFAULT_ANALYTICS_ACTOR_TYPE: ActorType.All = ActorType.All;
export const DEFAULT_PAGE_ANALYTICS_ACTOR_TYPE: ActorType.All = ActorType.All;
export type PageAnalyticsMentionsViewState = 'all' | 'viewed' | 'not-viewed';
export const DEFAULT_PAGE_ANALYTICS_MENTIONS_VIEW_STATE: PageAnalyticsMentionsViewState = 'all';
export const DEFAULT_PAGE_ANALYTICS_ENGAGEMENT_FILTER: boolean | null = null;
export const DEFAULT_SITE_ANALYTICS_ACTIVITY_TYPES: SiteAnalyticsActivityType[] = [
	ActorType.Agent,
	ActorType.People,
];

export const getAnalyticsActorType = (actorType: ActorType): AnalyticsActorType => {
	switch (actorType) {
		case ActorType.Agent:
			return 'AGENTS';
		case ActorType.People:
			return 'PEOPLE';
		case ActorType.All:
		default:
			return 'ALL';
	}
};

export const getPageAnalyticsActorTypeQueryValue = (
	actorType: ActorType,
): PageAnalyticsActorTypeQueryValue => getAnalyticsActorType(actorType);

export const getGatedAnalyticsActorType = (
	actorType: ActorType,
	isAiAgentAnalyticsEnabled: boolean,
): AnalyticsActorType | undefined =>
	isAiAgentAnalyticsEnabled ? getAnalyticsActorType(actorType) : undefined;

export const getGatedPageAnalyticsActorTypeQueryValue = (
	actorType: ActorType,
	isEnabled: boolean,
): PageAnalyticsActorTypeQueryValue | undefined => getGatedAnalyticsActorType(actorType, isEnabled);

export const getGatedPageAnalyticsActorType = (
	actorType: ActorType,
	isEnabled: boolean,
): ActorType => (isEnabled ? actorType : ActorType.All);

export const getSiteAnalyticsActivityTypeQueryValue = (
	activityTypes: SiteAnalyticsActivityType[],
): SiteAnalyticsActivityTypeQueryValue =>
	activityTypes.map(getAnalyticsActorType).join(',') as SiteAnalyticsActivityTypeQueryValue;

export const getGatedSiteAnalyticsActivityTypeQueryValue = (
	activityTypes: SiteAnalyticsActivityType[],
	isEnabled: boolean,
): SiteAnalyticsActivityTypeQueryValue | undefined =>
	isEnabled ? getSiteAnalyticsActivityTypeQueryValue(activityTypes) : undefined;

export const getSiteAnalyticsActivityByUserActorType = (
	activityTypes: SiteAnalyticsActivityType[],
): ActorType => (activityTypes.length === 1 ? activityTypes[0] : ActorType.All);

export const getSiteAnalyticsActivityTypesFromActorType = (
	actorType: ActorType,
): SiteAnalyticsActivityType[] => {
	switch (actorType) {
		case ActorType.Agent:
			return [ActorType.Agent];
		case ActorType.People:
			return [ActorType.People];
		case ActorType.All:
		default:
			return DEFAULT_SITE_ANALYTICS_ACTIVITY_TYPES;
	}
};

export type DisplayType = 'total' | 'unique';
export type SpaceType = 'global' | 'personal' | 'collaboration' | 'knowledge_base';
export const ALL_SITE_SPACE_TYPES: SpaceType[] = [
	'global',
	'personal',
	'collaboration',
	'knowledge_base',
];

export const getDefaultSiteSpaceTypes = (tab: SiteAnalyticsTab): SpaceType[] => {
	if (tab === SiteAnalyticsTab.Spaces || tab === SiteAnalyticsTab.Users) {
		return ALL_SITE_SPACE_TYPES;
	}

	return ['global'];
};

export const isDefaultSiteSpaceTypeSelection = (spaceType: SpaceType[]): boolean => {
	if (spaceType.length === 1 && spaceType[0] === 'global') {
		return true;
	}

	return (
		spaceType.length === ALL_SITE_SPACE_TYPES.length &&
		ALL_SITE_SPACE_TYPES.every((type) => spaceType.includes(type))
	);
};
export type ContentType = 'page' | 'blogpost' | 'livedoc';

export type UserType = {
	accountId: string;
	userId: string;
	displayName: string;
	profilePictureUrl?: string;
	email?: string;
};
export type GroupType = {
	groupId: string;
	groupName: string;
	useAnalytics: boolean;
	profilePictureUrl?: string;
};
export type TeamType = {
	teamId: string;
	teamName: string;
	useAnalytics: boolean;
	profilePictureUrl?: string;
};
export type UserGroupType = UserType | GroupType | TeamType;
